import React, { useCallback, useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP } from "../lib/format";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ExternalLink, Plus, RefreshCw, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const inputCls = "w-full px-3 py-2 border border-border rounded-sm text-sm bg-background";
const CARD = "bg-card border border-border rounded-md p-4 sm:p-5";
const BTN_PRIMARY = "inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm disabled:opacity-60";
const BTN_GHOST = "inline-flex items-center gap-1 border border-border hover:bg-muted text-foreground rounded-sm px-2.5 py-1.5 text-[13px] disabled:opacity-50";
const DIAS = [["1", "Lunes"], ["2", "Martes"], ["3", "Miércoles"], ["4", "Jueves"], ["5", "Viernes"], ["6", "Sábado"], ["7", "Domingo"]];
const ESTADOS = {
  confirmada: ["Confirmada", "bg-success/15 text-success"],
  pendiente_pago: ["Pago pendiente", "bg-warning/15 text-warning"],
  expirada: ["Vencida sin pago", "bg-muted text-muted-foreground"],
  cancelada_sin_pago: ["Anulada sin pago", "bg-muted text-muted-foreground"],
  cancelada: ["Cancelada", "bg-destructive/10 text-destructive"],
  conflicto: ["Pagada sin hora", "bg-destructive/15 text-destructive"],
};
const RECORDATORIO_TXT = { 1440: "24 horas", 120: "2 horas", 60: "1 hora", 30: "30 minutos", 15: "15 minutos" };
const hoyIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Santiago" });
const fechaHora = (s) => {
  const [d, h] = s.split("T");
  const [y, m, dd] = d.split("-").map(Number);
  const t = new Date(y, m - 1, dd).toLocaleDateString("es-CL", { weekday: "short", day: "2-digit", month: "short" });
  return `${t.charAt(0).toUpperCase()}${t.slice(1)} ${h}`;
};

function Campo({ label, ayuda, children }) {
  return (
    <div>
      <label className="label-caps block mb-1">{label}</label>
      {children}
      {ayuda && <div className="text-[11px] text-muted-foreground mt-0.5">{ayuda}</div>}
    </div>
  );
}

export default function AgendaAdmin() {
  const [tab, setTab] = useState("reservas");
  const [estado, setEstado] = useState(null);

  const cargarEstado = useCallback(async () => {
    try {
      const { data } = await api.get("/agenda/admin");
      setEstado(data);
    } catch (e) {
      toast.error(extractError(e));
    }
  }, []);
  useEffect(() => { cargarEstado(); }, [cargarEstado]);

  return (
    <Page>
      <PageHeader kicker="Administración" title="Agenda de asesorías" testid="agenda-admin"
        subtitle="Reservas pagadas desde la página pública, horario de atención y precios."
        actions={estado && (
          <a href={estado.url_publica} target="_blank" rel="noreferrer" className={BTN_GHOST}>
            Ver página pública <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )} />
      <div className="inline-flex border border-border rounded-sm overflow-hidden text-[13px] mb-5">
        {[["reservas", "Reservas"], ["config", "Configuración"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 ${tab === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{l}</button>
        ))}
      </div>
      {!estado ? <div className="text-sm text-muted-foreground">Cargando…</div>
        : tab === "reservas" ? <Reservas estado={estado} />
          : <Configuracion estado={estado} onGuardado={cargarEstado} />}
    </Page>
  );
}

function Reservas({ estado }) {
  const [filtro, setFiltro] = useState("proximas");
  const [reservas, setReservas] = useState(null);
  const [abierta, setAbierta] = useState(null);
  const [cancelar, setCancelar] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const params = filtro === "proximas" ? { desde: hoyIso() } : {};
      const { data } = await api.get("/agenda/admin/reservas", { params });
      setReservas(filtro === "proximas" ? data.reservas : [...data.reservas].reverse());
    } catch (e) {
      toast.error(extractError(e));
    }
  }, [filtro]);
  useEffect(() => { cargar(); }, [cargar]);

  const confirmar = async (r) => {
    const nota = window.prompt("Confirmar sin pago en línea (por ejemplo, pagó por transferencia). Nota opcional:", "");
    if (nota === null) return;
    setOcupado(true);
    try {
      await api.post(`/agenda/admin/reservas/${r.id}/confirmar`, { nota });
      toast.success("Reserva confirmada y notificada");
      cargar();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setOcupado(false);
    }
  };

  const ejecutarCancelacion = async () => {
    setOcupado(true);
    try {
      const { data } = await api.post(`/agenda/admin/reservas/${cancelar.r.id}/cancelar`, {
        motivo: cancelar.motivo, devolver: cancelar.devolver, notificar: cancelar.notificar,
      });
      toast.success(data.devuelto ? "Reserva cancelada y pago devuelto" : "Reserva cancelada");
      setCancelar(null);
      cargar();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setOcupado(false);
    }
  };

  const conflictos = (reservas || []).filter((r) => r.estado === "conflicto");

  return (
    <div className="space-y-4">
      {!estado.config.activo && (
        <div className="text-[13px] text-warning flex gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" />
          La agenda pública está desactivada. Actívela en Configuración.</div>
      )}
      {conflictos.length > 0 && (
        <div className="text-[13px] text-destructive flex gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" />
          {conflictos.length} pago(s) recibidos sin hora disponible: coordine otra hora con el cliente o cancele con devolución.</div>
      )}
      <div className="flex items-center gap-2">
        <select className={`${inputCls.replace("w-full", "w-auto")}`} value={filtro} onChange={(e) => setFiltro(e.target.value)}>
          <option value="proximas">Próximas</option>
          <option value="todas">Todas (más recientes primero)</option>
        </select>
        <button className={BTN_GHOST} onClick={cargar}><RefreshCw className="h-3.5 w-3.5" /> Actualizar</button>
      </div>
      <div className={`${CARD} !p-0 overflow-x-auto`}>
        <table className="w-full text-[13px]">
          <thead className="text-muted-foreground text-left">
            <tr className="border-b border-border">
              <th className="px-3 py-2">Fecha</th><th className="px-3">Cliente</th><th className="px-3">Modalidad</th>
              <th className="px-3 text-right">Cuota</th><th className="px-3">Estado</th><th className="px-3">Pago</th><th className="px-3" />
            </tr>
          </thead>
          <tbody>
            {reservas === null && <tr><td colSpan={7} className="px-3 py-4 text-muted-foreground">Cargando…</td></tr>}
            {reservas?.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-muted-foreground">No hay reservas.</td></tr>}
            {reservas?.map((r) => {
              const [etq, cls] = ESTADOS[r.estado] || [r.estado, "bg-muted"];
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-b border-border/60 align-top">
                    <td className="px-3 py-2 whitespace-nowrap font-mono">{fechaHora(r.inicio)}</td>
                    <td className="px-3 py-2">
                      <button className="text-left hover:underline" onClick={() => setAbierta(abierta === r.id ? null : r.id)}>
                        <div className="font-medium">{r.cliente.nombre}</div>
                        <div className="text-muted-foreground">{r.cliente.email}</div>
                      </button>
                    </td>
                    <td className="px-3 py-2 capitalize">{r.modalidad}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.monto ? fmtCLP(r.monto) : "—"}</td>
                    <td className="px-3 py-2"><span className={`text-[11px] px-1.5 py-0.5 rounded-sm font-medium ${cls}`}>{etq}</span></td>
                    <td className="px-3 py-2 text-[12px] text-muted-foreground">
                      {r.pago?.proveedor || "—"}{r.pago?.payment_id ? ` · ${r.pago.payment_id}` : ""}
                      {r.devuelto ? " · devuelto" : ""}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right space-x-2">
                      {["pendiente_pago", "expirada", "cancelada_sin_pago"].includes(r.estado) && (
                        <button className={BTN_GHOST} disabled={ocupado} onClick={() => confirmar(r)}>Confirmar</button>
                      )}
                      {["pendiente_pago", "confirmada", "conflicto"].includes(r.estado) && (
                        <button className={BTN_GHOST} disabled={ocupado}
                          onClick={() => setCancelar({ r, motivo: "", devolver: r.estado !== "pendiente_pago" && r.pago?.proveedor === "mercadopago", notificar: true })}>
                          Cancelar
                        </button>
                      )}
                    </td>
                  </tr>
                  {abierta === r.id && (
                    <tr className="border-b border-border/60 bg-muted/30">
                      <td colSpan={7} className="px-3 py-2 text-[12px] space-y-0.5">
                        <div>Código {r.codigo} · Teléfono {r.cliente.telefono}{r.cliente.empresa ? ` · ${r.cliente.empresa}` : ""}</div>
                        {r.cliente.motivo && <div className="whitespace-pre-wrap">Motivo: {r.cliente.motivo}</div>}
                        {r.motivo_cancelacion && <div>Motivo de cancelación: {r.motivo_cancelacion}</div>}
                        {r.pago?.nota && <div>Nota: {r.pago.nota}</div>}
                        {r.recordatorios_log && (
                          <div>Recordatorios enviados: {Object.keys(r.recordatorios_log).sort((a, b) => b - a)
                            .map((m) => RECORDATORIO_TXT[m] || `${m} min`).join(", ")}
                            {r.cliente.whatsapp_recordatorios ? " · cliente pidió WhatsApp" : ""}</div>
                        )}
                        {r.correos_confirmacion && (
                          <div>Correos de confirmación: cliente {r.correos_confirmacion.cliente ? "enviado" : "no enviado"} ·
                            profesional {r.correos_confirmacion.profesional ? "enviado" : "no enviado"}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <AlertDialog open={!!cancelar} onOpenChange={(o) => !o && setCancelar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar reserva</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelar && `${cancelar.r.cliente.nombre} · ${fechaHora(cancelar.r.inicio)}. La hora vuelve a quedar disponible.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {cancelar && (
            <div className="space-y-2 text-[13px]">
              <input className={inputCls} placeholder="Motivo (se incluye en el correo)" value={cancelar.motivo}
                onChange={(e) => setCancelar({ ...cancelar, motivo: e.target.value })} />
              {cancelar.r.pago?.proveedor === "mercadopago" && cancelar.r.estado !== "pendiente_pago" && (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={cancelar.devolver} onChange={(e) => setCancelar({ ...cancelar, devolver: e.target.checked })} />
                  Devolver la cuota en Mercado Pago
                </label>
              )}
              {cancelar.r.estado !== "pendiente_pago" && (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={cancelar.notificar} onChange={(e) => setCancelar({ ...cancelar, notificar: e.target.checked })} />
                  Avisar por correo al cliente y al profesional
                </label>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction disabled={ocupado} onClick={(e) => { e.preventDefault(); ejecutarCancelacion(); }}>Cancelar reserva</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Configuracion({ estado, onGuardado }) {
  const [cfg, setCfg] = useState(estado.config);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setCfg(estado.config); }, [estado.config]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setMod = (m, k, v) => setCfg((c) => ({ ...c, modalidades: { ...c.modalidades, [m]: { ...c.modalidades[m], [k]: v } } }));
  const tramos = (d) => cfg.horario[d] || [];
  const setTramos = (d, t) => set("horario", { ...cfg.horario, [d]: t });

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put("/agenda/admin/config", {
        ...cfg,
        bloqueos: cfg.bloqueos.map((b) => ({ ...b, hora_desde: b.hora_desde || null, hora_hasta: b.hora_hasta || null })),
      });
      toast.success("Configuración guardada");
      onGuardado();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setGuardando(false);
    }
  };

  const mp = estado.mercadopago;
  return (
    <div className="space-y-5">
      <div className={`${CARD} grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-[13px]`}>
        <div>
          <div className="label-caps mb-1">Mercado Pago</div>
          {mp.configurado ? (
            <div className="text-success flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />
              Conectado ({mp.modo === "prueba" ? "credenciales de prueba" : "producción"})</div>
          ) : estado.pago_simulado ? (
            <div className="text-warning">Pago simulado (solo desarrollo)</div>
          ) : (
            <div className="text-warning flex gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Sin configurar: defina MERCADOPAGO_ACCESS_TOKEN en el .env del servidor. Sin él solo se aceptan reuniones sin costo.</div>
          )}
          {mp.webhook && <div className="text-[11px] text-muted-foreground mt-1 break-all">Webhook: {mp.webhook}
            {mp.firma_webhook ? " (firma validada)" : ""}</div>}
        </div>
        <div>
          <div className="label-caps mb-1">Correos (Brevo)</div>
          {estado.correo
            ? <div className="text-success flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Activo</div>
            : <div className="text-warning">Sin BREVO_API_KEY: no se enviarán notificaciones.</div>}
        </div>
        <div>
          <div className="label-caps mb-1">WhatsApp (recordatorios)</div>
          {estado.whatsapp?.configurado
            ? <div className="text-success flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />
                {estado.whatsapp.proveedor === "waha" ? "Activo · WAHA (no oficial)" : `Activo · Meta, plantilla «${estado.whatsapp.plantilla}»`}</div>
            : <div className="text-muted-foreground">Sin configurar: los recordatorios van solo por correo. El botón de WhatsApp para el cliente funciona igual.</div>}
        </div>
        <div>
          <div className="label-caps mb-1">Página pública</div>
          <a href={estado.url_publica} target="_blank" rel="noreferrer" className="underline break-all">{estado.url_publica}</a>
        </div>
      </div>

      <div className={`${CARD} space-y-4`}>
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={cfg.activo} onChange={(e) => set("activo", e.target.checked)} data-testid="agenda-activo" />
          Agenda pública activa (botón "Agendar asesoría" en el sitio)
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <Campo label="Título de la reunión"><input className={inputCls} value={cfg.titulo} onChange={(e) => set("titulo", e.target.value)} /></Campo>
          <Campo label="Profesional (nombre visible)"><input className={inputCls} value={cfg.profesional_nombre} onChange={(e) => set("profesional_nombre", e.target.value)} /></Campo>
          <Campo label="Correo del profesional" ayuda="Recibe cada reserva, recordatorios y cancelaciones.">
            <input type="email" className={inputCls} value={cfg.profesional_email} onChange={(e) => set("profesional_email", e.target.value)} />
          </Campo>
          <Campo label="Descripción"><input className={inputCls} value={cfg.descripcion} onChange={(e) => set("descripcion", e.target.value)} /></Campo>
          <Campo label="WhatsApp de la empresa" ayuda="Botón «Escribir por WhatsApp» que ve el cliente al confirmar su reserva y en los correos.">
            <input className={inputCls} placeholder="+56 9 1234 5678" value={cfg.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} />
          </Campo>
          <Campo label="WhatsApp del asesor" ayuda="Recibe los recordatorios por WhatsApp (debe ser distinto del número que envía los mensajes).">
            <input className={inputCls} placeholder="+56 9 1234 5678" value={cfg.profesional_whatsapp || ""} onChange={(e) => set("profesional_whatsapp", e.target.value)} />
          </Campo>
        </div>
        <div>
          <div className="label-caps mb-1">Recordatorios antes de la reunión</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
            {(estado.recordatorios_opciones || []).map((m) => (
              <label key={m} className="flex items-center gap-1.5">
                <input type="checkbox" checked={(cfg.recordatorios_min || []).includes(m)}
                  onChange={(e) => set("recordatorios_min", e.target.checked
                    ? [...(cfg.recordatorios_min || []), m] : (cfg.recordatorios_min || []).filter((x) => x !== m))} />
                {RECORDATORIO_TXT[m] || `${m} min`}
              </label>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Se envían al cliente y al asesor por correo{estado.whatsapp?.configurado ? " y por WhatsApp" : ""}. Si alguien reserva con poca anticipación, solo recibe los que aún correspondan.
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Campo label="Duración (min)"><input type="number" min="15" className={inputCls} value={cfg.duracion_min} onChange={(e) => set("duracion_min", Number(e.target.value))} /></Campo>
          <Campo label="Descanso entre (min)"><input type="number" min="0" className={inputCls} value={cfg.descanso_min} onChange={(e) => set("descanso_min", Number(e.target.value))} /></Campo>
          <Campo label="Anticipación (horas)" ayuda="Mínimo para reservar."><input type="number" min="0" className={inputCls} value={cfg.anticipacion_horas} onChange={(e) => set("anticipacion_horas", Number(e.target.value))} /></Campo>
          <Campo label="Ventana (días)" ayuda="Cuántos días hacia adelante."><input type="number" min="1" className={inputCls} value={cfg.ventana_dias} onChange={(e) => set("ventana_dias", Number(e.target.value))} /></Campo>
          <Campo label="Cancelación (horas)" ayuda="Plazo para cancelar en línea."><input type="number" min="0" className={inputCls} value={cfg.cancelacion_horas} onChange={(e) => set("cancelacion_horas", Number(e.target.value))} /></Campo>
        </div>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={cfg.devolucion_automatica} onChange={(e) => set("devolucion_automatica", e.target.checked)} />
          Devolver automáticamente la cuota en Mercado Pago cuando el cliente cancela dentro del plazo
        </label>
      </div>

      <div className={`${CARD} space-y-3`}>
        <div className="font-semibold text-[15px]">Modalidades y precios</div>
        <div className="grid md:grid-cols-2 gap-4">
          {[["online", "Online"], ["presencial", "Presencial"]].map(([k, l]) => (
            <div key={k} className="border border-border rounded-sm p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={cfg.modalidades[k].activo} onChange={(e) => setMod(k, "activo", e.target.checked)} /> {l}
              </label>
              <Campo label="Cuota (CLP, IVA incluido)" ayuda="0 = sin costo.">
                <input type="number" min="0" className={`${inputCls} font-mono`} value={cfg.modalidades[k].precio} onChange={(e) => setMod(k, "precio", Number(e.target.value))} />
              </Campo>
              {k === "online" ? (
                <Campo label="Enlace" ayuda="Enlace fijo de la videollamada (Google Meet o Zoom).">
                  <input className={inputCls} value={cfg.modalidades.online.enlace || ""} onChange={(e) => setMod("online", "enlace", e.target.value)} />
                </Campo>
              ) : (
                <div className="text-[12px] text-muted-foreground">
                  El punto de reunión se coordina con el cliente después de la reserva: los datos de contacto llegan en el correo de cada reserva.
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={`${CARD} space-y-2`}>
        <div className="font-semibold text-[15px]">Horario semanal (hora de Chile)</div>
        {DIAS.map(([d, nombre]) => (
          <div key={d} className="flex flex-wrap items-center gap-2 text-[13px] py-1 border-b border-border/50 last:border-0">
            <div className="w-24 font-medium">{nombre}</div>
            {tramos(d).length === 0 && <span className="text-muted-foreground">Sin atención</span>}
            {tramos(d).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1">
                <input type="time" className={inputCls.replace("w-full", "w-auto")} value={t[0]}
                  onChange={(e) => setTramos(d, tramos(d).map((x, j) => (j === i ? [e.target.value, x[1]] : x)))} />
                –
                <input type="time" className={inputCls.replace("w-full", "w-auto")} value={t[1]}
                  onChange={(e) => setTramos(d, tramos(d).map((x, j) => (j === i ? [x[0], e.target.value] : x)))} />
                <button className="text-muted-foreground hover:text-destructive" onClick={() => setTramos(d, tramos(d).filter((_, j) => j !== i))}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <button className={BTN_GHOST} onClick={() => setTramos(d, [...tramos(d), ["09:00", "13:00"]])}><Plus className="h-3.5 w-3.5" /> Tramo</button>
          </div>
        ))}
      </div>

      <div className={`${CARD} space-y-2`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-[15px]">Bloqueos</div>
            <div className="text-[11px] text-muted-foreground">Vacaciones, feriados o tramos ocupados. Sin horas = día completo.</div>
          </div>
          <button className={BTN_GHOST} onClick={() => set("bloqueos", [...cfg.bloqueos, { desde: hoyIso(), hasta: hoyIso(), hora_desde: "", hora_hasta: "", motivo: "" }])}>
            <Plus className="h-3.5 w-3.5" /> Bloqueo
          </button>
        </div>
        {cfg.bloqueos.length === 0 && <div className="text-[13px] text-muted-foreground">Sin bloqueos.</div>}
        {cfg.bloqueos.map((b, i) => {
          const cambiar = (k, v) => set("bloqueos", cfg.bloqueos.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
          return (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-1.5 items-center text-[13px]">
              <input type="date" className={`${inputCls} sm:col-span-2`} value={b.desde} onChange={(e) => cambiar("desde", e.target.value)} />
              <input type="date" className={`${inputCls} sm:col-span-2`} value={b.hasta} onChange={(e) => cambiar("hasta", e.target.value)} />
              <input type="time" className={`${inputCls} sm:col-span-2`} value={b.hora_desde || ""} onChange={(e) => cambiar("hora_desde", e.target.value)} />
              <input type="time" className={`${inputCls} sm:col-span-2`} value={b.hora_hasta || ""} onChange={(e) => cambiar("hora_hasta", e.target.value)} />
              <input className={`${inputCls} sm:col-span-3`} placeholder="Motivo (interno)" value={b.motivo || ""} onChange={(e) => cambiar("motivo", e.target.value)} />
              <button className="text-muted-foreground hover:text-destructive flex justify-center" onClick={() => set("bloqueos", cfg.bloqueos.filter((_, j) => j !== i))}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      <div className={`${CARD} space-y-2`}>
        <Campo label="Condiciones de la reserva" ayuda="Se muestran al reservar y en los correos.">
          <textarea rows={3} className={inputCls} value={cfg.politica} onChange={(e) => set("politica", e.target.value)} />
        </Campo>
      </div>

      <button className={BTN_PRIMARY} onClick={guardar} disabled={guardando} data-testid="agenda-guardar">
        {guardando ? "Guardando…" : "Guardar configuración"}
      </button>
    </div>
  );
}
